/**
 * Folder TanStack Query hooks.
 */

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { folderKeys } from "@/queries/keys";
import { getFolderList, getFolderDetail, getFolderViews } from "./api";

export type {
  ViewColumn,
  FolderView,
  Folder,
  FolderListResponse,
} from "./api";
import type { FolderListResponse } from "./api";

export interface FolderListFilters {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: string;
  query?: string;
  sourceType?: string;
  from?: string;
  to?: string;
  assignTo?: string[];
}

// -- Query Hooks -----------------------------------------------------

/** Fetch paginated folder list. */
export function useFolderList(
  filters: FolderListFilters = {},
  options?: Partial<UseQueryOptions<FolderListResponse>>
) {
  const {
    page = 0,
    pageSize = 0,
    sortBy = "showOrder",
    sortDir = "asc",
    query = "",
    sourceType = "",
    from = "",
    to = "",
    assignTo = [],
  } = filters;

  return useQuery({
    queryKey: folderKeys.list({
      page,
      pageSize,
      sortBy,
      sortDir,
      query,
      sourceType,
      from,
      to,
      assignTo,
    }),
    queryFn: async () => {
      const res = await getFolderList({
        page,
        pageSize,
        sortBy,
        sortDir,
        query,
        sourceType,
        from,
        to,
        assignTo,
      });
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch folder list");
      return res.data!;
    },
    ...options,
  });
}

/** Fetch a single folder by ID. */
export function useFolderDetail(id: string | undefined) {
  return useQuery({
    queryKey: folderKeys.detail(id!),
    queryFn: async () => {
      const res = await getFolderDetail(id!);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch folder detail");
      return res.data!;
    },
    // "default" is a route sentinel, not a real folder id.
    enabled: !!id && id !== "default",
  });
}

/** Fetch saved views for a folder. Server returns FolderView[] from the folderviews collection. */
export function useFolderViews(folderId: string | undefined) {
  return useQuery({
    queryKey: folderKeys.views(folderId!),
    queryFn: async () => {
      const res = await getFolderViews(folderId!);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch folder views");
      return res.data ?? [];
    },
    enabled: !!folderId,
  });
}
